import { ChangeDetectorRef, Component, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';

import { NgxSpinnerService } from 'ngx-spinner';

import { forkJoin, Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { mapAsBody } from '../../../shared/util/request-util';
import { TransportItem } from '../../../../models/transport-item.model';
import { PATHS } from '../../../../models/bary-paths.model';
import { TransportFormGroup } from '../../../../models/forms/TransportFormGroup';
import { OfferEstimation } from '../../../../models/offer-estimation.model';
import { Transporter } from '../../../../models/transporter.model';
import { HandlingTypeViewModel } from '../../../../models/handling-type-view.model';
import { Transport } from '../../../../models/transport.model';
import { TransportViewDTO } from '../../../../models/transport-view-dto.model';

import { CurrentDataService } from '../../../../services/current-data-service';
import { BaryModalService } from '../../../../services/bary-modal.service';
import { BaryFormFactoryService } from '../../../../services/bary-form-factory.service';
import { OfferHandlingTypeExtendedService } from '../../../../services/offer-handling-type-extended.service';
import { TransportExtendedService } from '../../../../services/transport-extended.service';
import { EstimateService } from '../../../../services/estimate.service';

import { FormArray, FormGroup } from '@angular/forms';
import { TRANSPORT_ITEM_ESTIMATE_STEP } from '../../../../models/enums/transport-item-estimate-step.enum';
import { ServiceType, ServiceTypeEnum } from '../../../../models/enums/offer-service-type.enum';

@Component({
  selector: 'jhi-wepal-light-estimate',
  templateUrl: './wepal-light-estimate.component.html',
  styleUrls: ['./wepal-light-estimate.component.scss'],
})
export class WepalLightEstimateComponent implements OnInit {
  readonly #router = inject(Router);
  readonly #currentDataService = inject(CurrentDataService);
  readonly #transportService = inject(TransportExtendedService);
  readonly #bff = inject(BaryFormFactoryService);
  readonly #spinnerService = inject(NgxSpinnerService);
  readonly #estimateService = inject(EstimateService);
  readonly #modalService = inject(BaryModalService);
  readonly #offerHandlingTypeService = inject(OfferHandlingTypeExtendedService);
  readonly #cdr = inject(ChangeDetectorRef);

  @ViewChild('card') card!: ElementRef;

  public transportFormGroup!: TransportFormGroup;
  public handlingTypes?: HandlingTypeViewModel[] = [];
  public quotationOK = false;
  public loadingData = false;
  public offerEstimations: OfferEstimation[] = [];
  public transport!: Transport;
  public debouncerRefreshPrice: Subject<string> = new Subject<string>();
  public spinnerPriceName = 'priceRefreshing';
  public transporter!: Transporter;

  public ngOnInit(): void {
    forkJoin({
      transport: this.#transportService.createEmpty(),
      transporter: this.#currentDataService.getCurrentTransporter(),
      customer: this.#currentDataService.getCustomer(),
      handlingTypes: this.#offerHandlingTypeService.getCurrent(),
    }).subscribe(data => {
      this.transporter = data.transporter;
      this.handlingTypes = data.handlingTypes;
      // build the formGroup
      this.transportFormGroup = this.#bff.makeTransportForm(data.transporter!, data.transport);
      // define refreshment conditions
      this.defineRefreshingConditions();
      this.debouncerRefreshPrice.pipe(debounceTime(400)).subscribe(() => {
        this.refreshOffer();
      });
      this.refreshOffer();
    });
  }

  // define changes that might refresh the price
  private defineRefreshingConditions(): void {
    this.transportFormGroup
      .getAngularForm()
      .valueChanges.pipe(debounceTime(300))
      .subscribe(() => {
        this.refreshOffer();
      });
  }

  public refreshOffer(): void {
    if (this.transportFormGroup.value()) {
      const transport = new Transport(this.transportFormGroup.getAngularForm().getRawValue());
      // check
      this.quotationOK = transport.checkItems() && transport.checkAddresses();
      if (!this.quotationOK) {
        this.#spinnerService.hide(this.spinnerPriceName).then(() => (this.loadingData = false));
        return;
      }
      this.#spinnerService.show(this.spinnerPriceName).then(() => (this.loadingData = true));
      this.doEstimatePricing(transport);
    }
  }

  public initTransportItem(index?: number): void {
    const transportItemFormValue: any = index || index === 0 ? this.transportFormGroup.controls.items[index].value : {};
    this.#modalService
      .openModalEstimate(this.transportFormGroup, this.currentStep, new TransportItem(transportItemFormValue))
      .action()
      .subscribe(res => {
        if (res.submit) {
          if (index || index === 0) {
            // MAJ du transportItem
            this.transportFormGroup.controls.items[index] = res.value;
            (this.transportFormGroup.getAngularForm().controls['items'] as FormArray).controls[index] = res.value;
          } else {
            //Ajout du transportItem
            this.transportFormGroup.controls.items.push(res.value);
            (this.transportFormGroup.getAngularForm().controls['items'] as FormArray).push(res.value);
          }
          this.updatePrice();
        }
      });
  }

  private get currentStep(): TRANSPORT_ITEM_ESTIMATE_STEP {
    if (this.transportFormGroup.controls.isControlledTemperature.value || this.transportFormGroup.controls.isHazardousMaterial.value) {
      return TRANSPORT_ITEM_ESTIMATE_STEP.SPECIAL;
    }
    return this.transportFormGroup.controls.items.length > 0 ? TRANSPORT_ITEM_ESTIMATE_STEP.ITEM : TRANSPORT_ITEM_ESTIMATE_STEP.SETTING;
  }

  public handleRemove(index: number): void {
    this.transportFormGroup.removeTransportItem(index);
    if (this.transportFormGroup.controls.items.length === 0) {
      this.offerEstimations = [];
    }
    this.updatePrice();
  }

  private updatePrice(): void {
    this.debouncerRefreshPrice.next();
  }

  public updateTransport(offerId: number): void {
    this.transport = new Transport({ ...this.transport, ...this.transportFormGroup.getAngularForm().getRawValue() });
    this.#transportService.applyOffer(offerId, this.transport).subscribe({
      next: (newTransport: TransportViewDTO) => {
        this.transportFormGroup = this.#bff.makeTransportForm(this.transporter!, newTransport);
        this.#currentDataService.updateCurrentTransport(newTransport);
        this.#currentDataService.updateTransportInModification(undefined);
        this.#router.navigateByUrl(PATHS.ESTIMATE.path);
      },
    });
  }

  private doEstimatePricing(transport: Transport): void {
    this.#estimateService
      .estimatePricing(transport)
      .pipe(mapAsBody)
      .subscribe({
        next: offerEstimations => {
          this.offerEstimations = offerEstimations || [];
          this.#spinnerService.hide();
          this.#cdr.detectChanges();
        },
      })
      .add(() => {
        this.#spinnerService.hide(this.spinnerPriceName).then(() => {
          this.loadingData = false;
        });
      });
  }

  public getValueForTransportItem(transportItemForm: FormGroup): any {
    return transportItemForm.getRawValue();
  }
}
